import { http } from '@/lib/http';

export type EmployeeStatus = 'active' | 'inactive';

/** Mirrors `repo.Employee` in the vizitpro-api swagger spec. */
export interface EmployeeDto {
  id: string;
  business_id: string;
  user_id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  title: string | null;
  status: EmployeeStatus;
  hired_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Request body for `POST /businesses/{business_id}/employees` — mirrors
 * `controller.createEmployeeRequest`. `full_name` is required (min length
 * 1); the rest are optional.
 */
export interface CreateEmployeeRequest {
  full_name: string;
  email?: string;
  phone?: string;
  title?: string;
  hired_at?: string;
}

/**
 * Request body for `PATCH /businesses/{business_id}/employees/{id}` —
 * mirrors `controller.updateEmployeeRequest`. Only `title`, `status`, and
 * `hired_at` are editable here — `full_name`/`email`/`phone` are
 * denormalized from the linked user record and not settable via this
 * endpoint.
 */
export interface UpdateEmployeeRequest {
  title?: string;
  status?: EmployeeStatus;
  hired_at?: string;
}

export function employeesQueryKey(businessId: string, status: EmployeeStatus | 'all') {
  return ['employees', businessId, status] as const;
}

export function employeeQueryKey(businessId: string, employeeId: string) {
  return ['employees', businessId, employeeId] as const;
}

/** Calls `GET /businesses/{business_id}/employees`, optionally filtered by status. */
export async function listEmployees(
  businessId: string,
  status?: EmployeeStatus,
): Promise<EmployeeDto[]> {
  const { data } = await http.get<EmployeeDto[]>(
    `/businesses/${businessId}/employees`,
    { params: status ? { status } : undefined },
  );
  return data;
}

/** Calls `GET /businesses/{business_id}/employees/{id}`. */
export async function getEmployee(
  businessId: string,
  employeeId: string,
): Promise<EmployeeDto> {
  const { data } = await http.get<EmployeeDto>(
    `/businesses/${businessId}/employees/${employeeId}`,
  );
  return data;
}

/** Calls `POST /businesses/{business_id}/employees`. Caller must be an owner. */
export async function createEmployee(
  businessId: string,
  body: CreateEmployeeRequest,
): Promise<EmployeeDto> {
  const { data } = await http.post<EmployeeDto>(
    `/businesses/${businessId}/employees`,
    body,
  );
  return data;
}

/** Calls `PATCH /businesses/{business_id}/employees/{id}`. Caller must be an owner. */
export async function updateEmployee(
  businessId: string,
  employeeId: string,
  body: UpdateEmployeeRequest,
): Promise<EmployeeDto> {
  const { data } = await http.patch<EmployeeDto>(
    `/businesses/${businessId}/employees/${employeeId}`,
    body,
  );
  return data;
}

/**
 * Response of `POST /businesses/{business_id}/employees/{id}/invite` —
 * mirrors `controller.inviteEmployeeResponse`. `link` is a Firebase
 * passwordless email sign-in link for the employee to activate their
 * account. Unlike `QuoteShareLinkDto`, the backend does not return an
 * expiry for this link.
 */
export interface EmployeeInviteLinkDto {
  link: string;
}

/**
 * Calls `POST /businesses/{business_id}/employees/{id}/invite` (empty
 * body). Generates a fresh sign-in link for the employee. Caller must be
 * an owner. 400s if the employee has no email on file.
 */
export async function inviteEmployee(
  businessId: string,
  employeeId: string,
): Promise<EmployeeInviteLinkDto> {
  const { data } = await http.post<EmployeeInviteLinkDto>(
    `/businesses/${businessId}/employees/${employeeId}/invite`,
  );
  return data;
}
